// D9 LALA-UNDO (her YES, 2026-10-27): A DELETED ASK LALA CONVERSATION CAN BE BROUGHT BACK FOR 30 DAYS.
//
// Seat 152873 lost a two-month reconciliation thread to its own delete button (#446) and nothing could bring it back. The
// server now keeps a deleted thread whole for thirty days (`lala_conversation_trash`); this is the one place she sees them,
// under her conversation list, each with a Restore button. Its own component so the Ask Lala page gains three lines, not
// a fourth hook-heavy block (Item 7: App.tsx is refactored opportunistically as areas are touched).
import { useEffect, useState } from "react";

type Trashed = { id: string; conversationId: string; title: string; messageCount: number; restoreUntil: string };

const until = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

export function LalaRecentlyDeleted({ refreshKey, onRestored }: { refreshKey: number; onRestored: (conversationId: number) => void }) {
  const [items, setItems] = useState<Trashed[]>([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => {
    let live = true;
    fetch("/api/lala/conversations/trash", { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => { if (live && data && Array.isArray(data.trash)) setItems(data.trash); })
      .catch(() => {});
    return () => { live = false; };
  }, [refreshKey]);

  if (!items.length) return null;

  async function restore(t: Trashed) {
    setBusy(t.id);
    setNote("");
    try {
      const r = await fetch(`/api/lala/conversations/trash/${encodeURIComponent(t.id)}/restore`, { method: "POST", credentials: "same-origin" });
      const data = r.ok ? await r.json() : null;
      if (data?.ok && data.conversation?.id) {
        setItems((all) => all.filter((x) => x.id !== t.id));
        onRestored(Number(data.conversation.id));
      } else {
        setNote(data?.message || "That conversation could not be restored just now. Please try again.");
      }
    } catch {
      setNote("That conversation could not be restored just now. Please try again.");
    } finally {
      setBusy("");
    }
  }

  return (
    <div className="ask-lala-collection ask-lala-trash">
      <button type="button" className="ask-lala-trash__toggle" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span className="ask-lala-collection__name">Recently deleted ({items.length})</span>
      </button>
      {open ? (
        <>
          {items.map((t) => (
            <div key={t.id} className="ask-lala-convo ask-lala-trash__item">
              <span className="ask-lala-convo__open ask-lala-trash__title" title={`Can be restored until ${until(t.restoreUntil)}`}>{t.title || "Untitled"}</span>
              <button type="button" className="ask-lala-trash__restore" onClick={() => restore(t)} disabled={busy === t.id}>
                {busy === t.id ? "Restoring" : "Restore"}
              </button>
            </div>
          ))}
          <p className="ask-lala-sidebar__empty">Deleted conversations stay here for 30 days.</p>
          {note ? <p className="ask-lala-sidebar__empty" role="status">{note}</p> : null}
        </>
      ) : null}
    </div>
  );
}
